import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CalendarDays, Check, ChevronRight, Clock, CreditCard, Loader2, MapPin, Monitor, PartyPopper, Users } from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormatBadge, fmtDate } from "@/components/UpcomingWorkshops";
import { fetchWorkshop, initWorkshopSeat, verifyWorkshopSeat } from "@/lib/api";
import { breadcrumbJsonLd, serviceJsonLd } from "@/lib/seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function loadRazorpay() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

function BookSeat({ w }) {
    const [form, setForm] = useState({ full_name: "", mobile: "", email: "", company: "", city: "", profession: "" });
    const [step, setStep] = useState("form");
    const [paying, setPaying] = useState(false);
    const [done, setDone] = useState(null);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const inputCls = "mt-2 h-12 border-white/10 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-gold-300/40";

    useEffect(() => { loadRazorpay(); }, []);

    if (w.sold_out) {
        return (
            <div data-testid="workshop-soldout" className="glass glow-border rounded-lg p-10 text-center">
                <h3 className="font-display text-2xl">This workshop is <span className="text-red-400">SOLD OUT</span></h3>
                <p className="mt-3 text-sm text-white/60">Follow the workshops page for the next batch, or book a consultation.</p>
                <Link to="/consultation" className="gold-button mt-6 inline-flex items-center gap-2">Book Your Consultation <ArrowRight size={14} /></Link>
            </div>
        );
    }
    if (done) {
        return (
            <div data-testid="workshop-booking-done" className="glass glow-border rounded-lg p-10 text-center">
                <PartyPopper className="mx-auto text-gold-300" size={34} />
                <h3 className="mt-5 font-display text-2xl">Seat booked!</h3>
                <p className="mx-auto mt-3 max-w-md text-sm text-white/65">
                    Booking ID: <span className="gold-text font-semibold">{done.booking_code}</span>. Confirmation details will
                    reach you on {form.mobile} / {form.email}.
                    {done.meeting_link && <> Meeting link: <a href={done.meeting_link} className="text-gold-300 underline" target="_blank" rel="noreferrer">{done.meeting_link}</a></>}
                </p>
            </div>
        );
    }

    const pay = async () => {
        setPaying(true);
        try {
            const ok = await loadRazorpay();
            if (!ok) throw new Error("razorpay");
            const order = await initWorkshopSeat(w.slug, form);
            const rzp = new window.Razorpay({
                key: order.razorpay_key_id, amount: order.amount * 100, currency: "INR",
                name: "Abhijit JK", description: order.workshop_title.slice(0, 100), order_id: order.razorpay_order_id,
                prefill: { name: form.full_name, email: form.email, contact: form.mobile },
                theme: { color: "#D4AF37" },
                handler: async (resp) => {
                    try {
                        const v = await verifyWorkshopSeat({
                            booking_id: order.booking_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_signature: resp.razorpay_signature,
                        });
                        setDone(v);
                        toast.success("Payment successful! Your seat is booked.");
                    } catch { toast.error("Payment verification failed. Contact support."); }
                    finally { setPaying(false); }
                },
                modal: { ondismiss: () => setPaying(false) },
            });
            rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setPaying(false); });
            rzp.open();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start payment. Please try again.");
            setPaying(false);
        }
    };

    return (
        <div data-testid="workshop-book-widget" className="glass glow-border rounded-lg p-8 md:p-10">
            {step === "form" ? (
                <>
                    <h3 className="font-display text-2xl">Book Your <span className="gold-text">Seat</span></h3>
                    <div className="mt-6 grid gap-5 sm:grid-cols-2">
                        <div><Label className="text-white/75">Full name *</Label><Input data-testid="ws-name" value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="Your name" /></div>
                        <div><Label className="text-white/75">Mobile *</Label><Input data-testid="ws-mobile" value={form.mobile} onChange={set("mobile")} className={inputCls} placeholder="10-digit mobile" /></div>
                        <div><Label className="text-white/75">Email *</Label><Input data-testid="ws-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" /></div>
                        <div><Label className="text-white/75">Company</Label><Input data-testid="ws-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Optional" /></div>
                        <div><Label className="text-white/75">City</Label><Input value={form.city} onChange={set("city")} className={inputCls} placeholder="Optional" /></div>
                        <div><Label className="text-white/75">Profession / Business</Label><Input value={form.profession} onChange={set("profession")} className={inputCls} placeholder="Optional" /></div>
                    </div>
                    <button
                        data-testid="ws-continue"
                        onClick={() => {
                            if (!form.full_name || !form.mobile || !form.email) return toast.error("Please fill name, mobile and email.");
                            setStep("summary");
                        }}
                        className="gold-button mt-8 inline-flex w-full items-center justify-center gap-2 sm:w-auto"
                    >
                        Continue <ArrowRight size={14} />
                    </button>
                </>
            ) : (
                <>
                    <h3 className="font-display text-2xl">Order <span className="gold-text">Summary</span></h3>
                    <div className="mt-6 space-y-3 rounded-md border border-white/10 bg-white/5 p-6 text-sm text-white/75">
                        <p className="flex justify-between"><span>Workshop</span><span className="text-right font-medium text-white">{w.title}</span></p>
                        {w.start_date && <p className="flex justify-between"><span>Date</span><span>{fmtDate(w.start_date)}{w.end_date && w.end_date !== w.start_date ? ` – ${fmtDate(w.end_date)}` : ""}</span></p>}
                        <p className="flex justify-between"><span>Participant</span><span>{form.full_name}</span></p>
                        <p className="flex justify-between"><span>Price ({w.price.label})</span><span>₹{w.price.base.toLocaleString("en-IN")}</span></p>
                        {w.price.gst > 0 && <p className="flex justify-between"><span>GST ({w.gst_percent}%)</span><span>₹{w.price.gst.toLocaleString("en-IN")}</span></p>}
                        <p className="flex justify-between border-t border-white/10 pt-3 font-semibold text-white"><span>Total</span><span className="gold-text" data-testid="ws-total">₹{w.price.total.toLocaleString("en-IN")}</span></p>
                    </div>
                    <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                        <button data-testid="ws-pay-now" onClick={pay} disabled={paying} className="gold-button inline-flex items-center justify-center gap-2 disabled:opacity-60">
                            {paying ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={14} />} Pay ₹{w.price.total.toLocaleString("en-IN")} Securely
                        </button>
                        <button onClick={() => setStep("form")} className="ghost-button">Back</button>
                    </div>
                </>
            )}
        </div>
    );
}

export default function WorkshopDetail() {
    const { slug } = useParams();
    const [w, setW] = useState(null);
    const [err, setErr] = useState(false);
    useEffect(() => {
        setW(null); setErr(false);
        fetchWorkshop(slug).then(setW).catch(() => setErr(true));
    }, [slug]);

    if (err) return <Navigate to="/workshops" replace />;
    if (!w) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-gold-300" size={28} /></div>;

    const scrollBook = () => document.getElementById("book-seat")?.scrollIntoView({ behavior: "smooth" });

    return (
        <div data-testid="workshop-detail-page" className="relative">
            <Seo
                title={w.seo_title || `${w.title} | Abhijit JK`}
                description={w.meta_description || w.short_description || w.headline}
                image={w.image ? `${BACKEND}${w.image}` : "/images/workshop-hero.jpg"}
                path={`/workshops/${w.slug}`}
                jsonLd={[
                    serviceJsonLd({ name: w.title, description: w.meta_description || w.short_description, price: w.price.total, url: `/workshops/${w.slug}` }),
                    breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Workshops", path: "/workshops" }, { name: w.title, path: `/workshops/${w.slug}` }]),
                ]}
            />
            <nav aria-label="Breadcrumb" className="mx-auto max-w-7xl px-6 pt-8 lg:px-10">
                <ol className="flex flex-wrap items-center gap-1.5 text-xs text-white/45">
                    <li><Link to="/" className="hover:text-gold-300">Home</Link></li>
                    <li className="flex items-center gap-1.5"><ChevronRight size={12} /><Link to="/workshops" className="hover:text-gold-300">Workshops</Link></li>
                    <li className="flex items-center gap-1.5"><ChevronRight size={12} /><span className="text-white/70">{w.title}</span></li>
                </ol>
            </nav>

            {/* HERO */}
            <section className="mx-auto grid max-w-7xl items-center gap-10 px-6 pt-10 pb-14 lg:grid-cols-12 lg:px-10">
                <div className="lg:col-span-6">
                    <FormatBadge format={w.format} />
                    <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.06] tracking-tighter sm:text-5xl">{w.title}</h1>
                    {w.headline && <p className="mt-4 font-display text-xl text-gold-300/90">{w.headline}</p>}
                    {w.subheadline && <p className="mt-4 max-w-xl text-base text-white/65">{w.subheadline}</p>}
                    <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/65">
                        {w.start_date && <span className="inline-flex items-center gap-2"><CalendarDays size={14} className="text-gold-300" /> Starts: {fmtDate(w.start_date)}</span>}
                        {w.duration_label && <span className="inline-flex items-center gap-2"><Clock size={14} className="text-gold-300" /> {w.duration_label}</span>}
                        {w.start_time && <span>{w.start_time}{w.end_time ? ` – ${w.end_time}` : ""} {w.timezone}</span>}
                        {w.format !== "online" && w.city && <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-gold-300" /> {w.city}</span>}
                    </div>
                    <div className="mt-6 flex items-center gap-4">
                        {w.strike_price && <span className="text-lg text-white/40 line-through">₹{w.strike_price.toLocaleString("en-IN")}</span>}
                        <span data-testid="workshop-detail-price" className="gold-text font-display text-3xl font-semibold">₹{w.price.base.toLocaleString("en-IN")}</span>
                        {w.price.label !== "Regular" && <span className="rounded-full bg-gold-300/10 px-3 py-1 text-xs text-gold-300">{w.price.label}</span>}
                        {typeof w.seats_left === "number" && !w.sold_out && <span className="inline-flex items-center gap-1.5 text-sm text-gold-300"><Users size={13} /> {w.seats_left} seats left</span>}
                    </div>
                    <button onClick={scrollBook} data-testid="workshop-detail-book-cta" disabled={w.sold_out} className="gold-button mt-8 inline-flex items-center gap-2 disabled:opacity-50">
                        {w.sold_out ? "SOLD OUT" : "BOOK YOUR SEAT"} <ArrowRight size={14} />
                    </button>
                </div>
                <div className="lg:col-span-6">
                    <div className="overflow-hidden rounded-lg">
                        <img src={w.image ? `${BACKEND}${w.image}` : "/images/workshop-hero.jpg"} alt={`${w.title} — workshop by Abhijit JK`} className="h-full w-full object-cover" />
                    </div>
                </div>
            </section>

            {/* ABOUT + LEARN + WHO */}
            <section className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
                <div className="grid gap-8 lg:grid-cols-12">
                    <Reveal className="lg:col-span-7">
                        <div className="glass h-full rounded-lg p-8 md:p-10">
                            <h2 className="font-display text-2xl">About the <span className="gold-text">Workshop</span></h2>
                            <div className="mt-5 space-y-4">
                                {(w.description || "").split("\n\n").map((p, i) => (
                                    <p key={i} className="text-sm leading-relaxed text-white/70 md:text-base">{p}</p>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                    <div className="space-y-8 lg:col-span-5">
                        {w.learn_points?.length > 0 && (
                            <Reveal>
                                <div className="glass glow-border rounded-lg p-8">
                                    <h2 className="font-display text-xl">What You Will <span className="gold-text">Learn</span></h2>
                                    <ul className="mt-4 space-y-2.5">
                                        {w.learn_points.map((p) => <li key={p} className="flex items-start gap-2.5 text-sm text-white/75"><Check size={14} className="mt-1 flex-none text-gold-300" />{p}</li>)}
                                    </ul>
                                </div>
                            </Reveal>
                        )}
                        {w.who_should_attend?.length > 0 && (
                            <Reveal>
                                <div className="glass rounded-lg p-8">
                                    <h2 className="font-display text-xl">Who Should <span className="gold-text">Attend</span></h2>
                                    <ul className="mt-4 space-y-2.5">
                                        {w.who_should_attend.map((p) => <li key={p} className="flex items-start gap-2.5 text-sm text-white/75"><Users size={14} className="mt-1 flex-none text-gold-300" />{p}</li>)}
                                    </ul>
                                </div>
                            </Reveal>
                        )}
                    </div>
                </div>
            </section>

            {/* SCHEDULE + VENUE */}
            <section className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
                <div className="grid gap-8 md:grid-cols-2">
                    <Reveal>
                        <div className="glass rounded-lg p-8">
                            <h2 className="font-display text-xl">Workshop <span className="gold-text">Schedule</span></h2>
                            <div className="mt-4 space-y-2 text-sm text-white/70">
                                {w.start_date && <p>Date: {fmtDate(w.start_date)}{w.end_date && w.end_date !== w.start_date ? ` – ${fmtDate(w.end_date)}` : ""}</p>}
                                {w.schedule_days?.length > 0 && <p>Days: {w.schedule_days.join(", ")}</p>}
                                {w.start_time && <p>Time: {w.start_time}{w.end_time ? ` – ${w.end_time}` : ""} {w.timezone}</p>}
                                {w.duration_label && <p>Duration: {w.duration_label}</p>}
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <div className="glass rounded-lg p-8">
                            <h2 className="font-display text-xl">{w.format === "online" ? <>Online <span className="gold-text">Details</span></> : <>Venue <span className="gold-text">Information</span></>}</h2>
                            <div className="mt-4 space-y-2 text-sm text-white/70">
                                {(w.format === "online" || w.format === "hybrid") && (
                                    <>
                                        {w.online_platform && <p className="inline-flex items-center gap-2"><Monitor size={13} className="text-gold-300" /> Platform: {w.online_platform}</p>}
                                        <p>Joining link and instructions are shared after successful booking.</p>
                                    </>
                                )}
                                {(w.format === "offline" || w.format === "hybrid") && (
                                    <>
                                        {w.venue_name && <p>{w.venue_name}</p>}
                                        {w.address && <p>{w.address}</p>}
                                        {(w.area || w.city) && <p>{[w.area, w.city].filter(Boolean).join(", ")}</p>}
                                        {w.maps_link && <a href={w.maps_link} target="_blank" rel="noreferrer" className="text-gold-300 underline">Open in Google Maps</a>}
                                    </>
                                )}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* BOOKING */}
            <section id="book-seat" className="mx-auto max-w-4xl px-6 py-16 lg:px-10">
                <BookSeat w={w} />
            </section>
        </div>
    );
}
